import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, HostListener } from "@angular/core";
import { ITCIAssetTag, TCI_ASSET_TAGS_COMPONENT_LAYOUT, TCI_ASSET_TAGS_COMPONENT_MODE, TAG_FILTER_SORT } from "@tci/common";
import { NgTagsService } from "../../ng-services/ng-tags.service";
import { NgSearchSelectTagsComponent } from "./ng-search-select-tags/ng-search-select-tags.component";
import * as lodash from "lodash";

@Component({
    selector: "ng-asset-tags",
    templateUrl: "./ng-asset-tags.component.html",
    styleUrls: ["./ng-asset-tags.component.less"]
})
export class NgAssetTagsComponent implements OnInit, OnChanges {
    @Input() mode: TCI_ASSET_TAGS_COMPONENT_MODE = TCI_ASSET_TAGS_COMPONENT_MODE.SELECT_ONLY;
    @Input() layout: TCI_ASSET_TAGS_COMPONENT_LAYOUT = TCI_ASSET_TAGS_COMPONENT_LAYOUT.HORIZONTAL_FLOW;
    @Input() allTags: ITCIAssetTag[] = [];
    @Input() selectedTags: ITCIAssetTag[] = [];
    @Input() cssClasses: string | string[] = [];
    @Input() tagsListWidth = "120px";
    @Input() tagsListHeight: string;
    @Input() tagNameLengthLimit = 30;
    @Input() selectAllEnabled = true;
    @Input() clearAllEnabled = true;
    @Input() showOnlySelected = true;
    @Input() limitedSet = false;

    @Input() showSelectedOnFiltering = false;
    @Input() sortSelectedOnFiltering: TAG_FILTER_SORT = TAG_FILTER_SORT.alphabetical;

    @Output() selectionChanged: EventEmitter<ITCIAssetTag[]> = new EventEmitter();
    @Output() beforeShow: EventEmitter<ITCIAssetTag[]> = new EventEmitter();
    @Output() afterHide: EventEmitter<ITCIAssetTag[]> = new EventEmitter();

    @ViewChild("ngAssetTagsRoot") rootElement: ElementRef;
    @ViewChild("readonlyTags") readonlyTags: NgSearchSelectTagsComponent;
    @ViewChild("editableTags") editableTags: NgSearchSelectTagsComponent;
    @ViewChild("editableTags", { read: ElementRef }) editableTagsRef: ElementRef;

    TCI_ASSET_TAGS_COMPONENT_MODE = TCI_ASSET_TAGS_COMPONENT_MODE;
    TCI_ASSET_TAGS_COMPONENT_LAYOUT = TCI_ASSET_TAGS_COMPONENT_LAYOUT;
    localAllTags: ITCIAssetTag[] = [];
    localSelectedTags: ITCIAssetTag[] = [];
    concatenatedSelectedTags = "";

    overlayIsShown = false;
    private overlayIsReady = false;

    ELEMENT_IDS = { pill: "tags-asset-pill" };

    // Tooltips
    showNameTooltip = false;
    tooltipOffsetLeft = "0";
    tooltipOffsetTop = "0";

    // Action tracking
    private localCreatedTags: ITCIAssetTag[] = [];

    constructor(private tagsService: NgTagsService, public el: ElementRef) { }

    ngOnInit() {
        this.localAllTags = lodash.cloneDeep(this.allTags);
        this.localSelectedTags = lodash.cloneDeep(this.selectedTags || []);
        this.applyCustomCssClasses();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.allTags) {
            this.localAllTags = lodash.cloneDeep(this.allTags);
        }

        if (changes.selectedTags) {
            this.localSelectedTags = lodash.cloneDeep(this.selectedTags || []);
        }

        if (
            (changes.allTags && this.localAllTags && this.localAllTags.length > 0) ||
            (changes.selectedTags && this.localSelectedTags && this.localSelectedTags.length > 0)
        ) {
            this.initTags();
        }
    }

    applyCustomCssClasses(): void {
        // Convert the CSS classes string into an array of CSS class name strings.
        if (lodash.isString(this.cssClasses)) {
            this.cssClasses = this.cssClasses.split(",");
        }

        lodash.forEach(this.cssClasses, (cssClassName: string) => {
            const element = this.rootElement.nativeElement;
            if (!element.classList.contains(cssClassName)) {
                element.classList.add(cssClassName);
            }
        });
    }

    changeTagsSelection(tags: ITCIAssetTag[]): void {
        for (let n = this.localCreatedTags.length - 1; n > -1; n--) { // Evaluate if a just-added tag was removed
            const indexCreated = tags.findIndex(t => t.name === this.localCreatedTags[n].name);
            if (indexCreated < 0) { // New tag is not part of the incoming collection
                if (this.localAllTags) {
                    const localIndex = this.localAllTags.findIndex(lat => lat.name === this.localCreatedTags[n].name);
                    this.localAllTags.splice(localIndex, 1);
                }

                this.localCreatedTags.splice(n, 1);
            }
        }

        this.localSelectedTags = tags;
        this.initTags();
        this.selectionChanged.emit(tags);
    }

    createNewTag(tag: ITCIAssetTag): void {
        this.localCreatedTags.push(tag);
        this.localAllTags = lodash.union(this.localAllTags, [tag]);
        this.localSelectedTags = lodash.union(this.localSelectedTags, [tag]);
        this.sortTags();
        this.initTags();
        this.selectionChanged.emit(this.localSelectedTags);
    }

    doAfterHide(): void { // Hide overlay
        this.overlayIsShown = false;
        this.overlayIsReady = false;
        this.afterHide.emit(this.localSelectedTags);
        this.sortTags();
        this.editableTags.sortTags();
        this.editableTags.resetKeyword();
    }

    doBeforeShow(): void { // Show overlay
        this.overlayIsShown = true;
        this.beforeShow.emit(this.localSelectedTags);
        this.editableTags.focusField();

        // Delay the component readyness to avoid clickOutside
        setTimeout(() => this.overlayIsReady = true);

        // Hide the tooltip
        const event = new MouseEvent("mouseleave", { "view": window, "bubbles": true, "cancelable": true });
        const pill = document.getElementById(this.ELEMENT_IDS.pill);
        pill.dispatchEvent(event);
    }

    doClickPill(): void {
        if (this.mode === TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL) {
            const overlayState = this.overlayIsShown;
            this.doBeforeShow();
            this.overlayIsShown = !overlayState;
        }
    }

    initTags(): void {
        this.removeNonExistentTags();
        this.updateConcatenatedSelectedTags();
    }

    removeNonExistentTags(): void {
        this.localSelectedTags = lodash.intersectionBy(this.localSelectedTags, this.localAllTags, "name");
    }

    sortTags(): void {
        this.localAllTags = lodash.sortBy(this.localAllTags, "name");
        this.localSelectedTags = lodash.sortBy(this.localSelectedTags, "name");
    }

    updateConcatenatedSelectedTags(): void {
        this.concatenatedSelectedTags = this.tagsService.concatTagNames(this.localSelectedTags);
    }

    // Name tooltip
    showTooltip($event: any) {
        const pill = document.getElementById(this.ELEMENT_IDS.pill);
        this.tooltipOffsetLeft = (pill.offsetLeft - 4) + "px";
        this.tooltipOffsetTop = (pill.offsetTop + 26) + "px";
        this.showNameTooltip = true;
    }

    hideTooltip() {
        this.showNameTooltip = false;
    }

    // Close overlay on interaction
    @HostListener("document:click", ["$event.target"])
    public onClick(targetElement) {
        if (this.overlayIsReady) {
            const clickedInside = this.editableTagsRef.nativeElement.contains(targetElement);
            const clickInPill = this.el.nativeElement.contains(targetElement);
            if (!clickedInside && !clickInPill) {
                this.doAfterHide();
            }
        }
    }
}
